/* eslint-disable camelcase */
import React from 'react';
import env from '@lib/envs/env';
import Compose from '@components/shared/Compose';
import LoginRegisterModalProvider
  from '@components/shared/providers/LoginRegisterModalProvider/LoginRegisterModalProvider';
import ShareModalProvider from '@components/shared/providers/ShareModalProvider/ShareModalProvider';
import ExploreProductModalProvider
  from '@components/shared/providers/ExploreProductModalProvider/ExploreProductModalProvider';
import AddedToCartConfirmationModalProvider from '@components/shared/providers/AddedToCartConfirmationModalProvider/AddedToCartConfirmationModalProvider';
import logger from '@lib/logger';
import { getApollo } from '@lib/apollo/apolloClient';
import {
  formatPrismicResponse,
  getFormattedMerchGroupUrl
} from '@components/pages/ShopLanding/model/prismic';
import { fetchShopNavigationMenu } from '@components/shared/shop/ShopNavigationMenu';
import GET_MERCH_GROUPS_BY_IDS
  from '@components/pages/ShopLanding/graphql/merch-groups-by-ids.graphql';
import GET_VENDOR_VARIANTS_BY_IDS
  from '@components/pages/ShopLanding/graphql/vendor-variants-by-id.graphql';
import AddToBoardModalProvider
  from '@components/shared/providers/AddToBoardModalProvider/AddToBoardModalProvider';
import { getMerchGroups } from '@components/shared/shop/model/MerchGroup';
import { getTopLevelShopGroups } from '@components/shared/shop/model/ShopGroup';
import useUpdatePreview from '@lib/prismic/util/useUpdatePreviewRef';
import ShopLanding from '@components/pages/ShopLanding/ShopLanding';
import {
  fetchFeaturedBoardsFromPrismicDoc
} from '@components/pages/ShopLanding/model/featuredBoards';
import {
  getCollectionFeatureMerchGroupIds,
  getFormattedMerchGroupUrlForCollectionFeature
} from '@components/pages/ShopLanding/model/collectionFeature';
import {
  getFeaturedMerchGroupsWithCtaUrl
} from '@components/pages/ShopLanding/model/featuredMerchGroups';
import {
  getFormattedCollectionWithProductCards
} from '@components/pages/ShopLanding/model/collectionWithProductCards';
import ThemeProvider from '@components/theme/ThemeProvider';
import { GetStaticProps } from 'next';
import { allFulfilled } from '@lib/promise';
import fetchSinglePrismicDocument from '@lib/prismic/fetchSinglePrismicDocument';
import { staticNotFound } from '@utils/navigation/serverSideHelpers';

const REVALIDATE_INTERVAL_SECONDS = env.STATIC_PAGE_DEFAULT_CACHE_SECONDS;

const ShopPage = (props: any) => {
  const { previewRef, docId } = props;
  useUpdatePreview(previewRef, docId);

  return (
    <Compose
      components={[
        ThemeProvider,
        LoginRegisterModalProvider,
        ShareModalProvider,
        ExploreProductModalProvider,
        AddToBoardModalProvider,
        AddedToCartConfirmationModalProvider
      ]}
    >
      <ShopLanding {...props} />
    </Compose>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const log = logger('Shop Landing');

  log.info('Regenerating static page');

  const { previewData } = context;

  const [pageDoc] = await allFulfilled([
    fetchSinglePrismicDocument('new-shop-landing-page', { previewData }),
  ]);

  if (!pageDoc) {
    log.error(`Unable to load data from prismic`);
    return staticNotFound(REVALIDATE_INTERVAL_SECONDS);
  }

  const docId = pageDoc.id;

  const {
    heroBanner,
    ourStories,
    collectionFeature,
    raisonDetre,
    designerHighlight,
    featuredMerchGroups,
    collectionWithProductCards
  } = formatPrismicResponse(pageDoc.data);

  const apolloClient = getApollo();

  const merchGroupIds: number[] = [
    ...[heroBanner.merchGroupId],
    ...getCollectionFeatureMerchGroupIds(collectionFeature),
    ...[designerHighlight.merchGroupId],
    ...featuredMerchGroups.merchGroups.map((mg) => mg.id),
    ...[collectionWithProductCards.merchGroupId]
  ];

  const [
    merchGroups,
    shopGroups,
    merchGroupsByIds,
    vendorVariantsByIds,
    featuredBoards,
  ] = await allFulfilled([
    getMerchGroups(apolloClient),
    getTopLevelShopGroups(apolloClient),
    apolloClient.query({
      query: GET_MERCH_GROUPS_BY_IDS,
      variables: {
        merchGroupIds,
      },
    }),
    apolloClient.query({
      query: GET_VENDOR_VARIANTS_BY_IDS,
      variables: {
        ids: collectionWithProductCards.vendorVariants.map((vv) => vv.vendorVariantId),
      },
    }),
    fetchFeaturedBoardsFromPrismicDoc(apolloClient, pageDoc),
  ]);

  const heroBannerCtaUrl = getFormattedMerchGroupUrl(merchGroupsByIds, heroBanner.merchGroupId);

  const designerHighlightCtaUrl
    = getFormattedMerchGroupUrl(merchGroupsByIds, designerHighlight.merchGroupId);

  const shopNavigationMenu = await fetchShopNavigationMenu({
    previewData,
    apolloClient,
    merchGroups,
    merchGroupCategories: shopGroups
  });

  return {
    props: {
      shopNavigationMenu,
      merchGroupIds,
      heroBanner: {
        ...heroBanner, ...{ ctaUrl: heroBannerCtaUrl }
      },
      collectionFeature: getFormattedMerchGroupUrlForCollectionFeature(
        merchGroupsByIds, collectionFeature
      ),
      raisonDetre,
      collectionWithProductCards: getFormattedCollectionWithProductCards(
        merchGroupsByIds, vendorVariantsByIds, collectionWithProductCards
      ),
      ourStories,
      featuredBoards,
      featuredMerchGroups: {
        title: featuredMerchGroups.title,
        merchGroups: getFeaturedMerchGroupsWithCtaUrl(merchGroupsByIds, featuredMerchGroups)
      },
      designerHighlight: {
        ...designerHighlight, ...{ ctaUrl: designerHighlightCtaUrl }
      },
      previewRef: (previewData as any)?.ref || null,
      docId
    },
    revalidate: REVALIDATE_INTERVAL_SECONDS,
  };
};

export default ShopPage;
